/**
 * Thanh tìm kiếm BĐS trên header — desktop bar + mobile sheet + popover bộ lọc.
 *
 * Luồng dữ liệu:
 * - State filters cục bộ; đồng bộ từ defaultFilters (URL) khi parent truyền vào.
 * - Mount: fetch categories và provinces từ API.
 * - Submit/navigateWithFilters: buildHomeSearchPath → navigate sang trang chủ có query.
 * - Autocomplete: chọn tin → chi tiết; chọn địa điểm → cập nhật filters + navigate.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { useNavigate } from 'react-router-dom';

import { categoryApi } from '../../features/categories/api/categoryApi';

import { locationApi } from '../../features/locations/api/locationApi';

import {

  AMENITY_FILTER_FIELDS,

  ROOM_SPEC_FILTER_FIELDS,

} from '../../shared/constants/property';

import { APP_ROUTES } from '../../shared/constants/routes';

import { TEXT } from '../../shared/languages';

import { useCloseOnScroll } from '../../shared/hooks/useCloseOnScroll';

import {

  buildHomeSearchPath,

  clearExtraSearchFilters,

  countExtraSearchFilters,

  DEFAULT_PROPERTY_SEARCH_FILTERS,

  getPricePresetId,

  getPricePresetValues,

} from '../../shared/utils/propertySearchParams';

import { SearchAutocomplete } from '../property/SearchAutocomplete';

import { SearchFilterFields } from './SearchFilterFields';

import { CategoryDropdown } from './CategoryDropdown';

import { ProvinceDropdown } from './ProvinceDropdown';

import { AnimatedPanel } from './AnimatedPanel';

import './HeaderSearch.css';



const emptyFilters = {

  ...DEFAULT_PROPERTY_SEARCH_FILTERS,

  sort: '',

  page: '',

};



const SheetField = ({ label, children, compact = false, className = '' }) => (

  <label

    className={`dropdown-sheet-field ${compact ? 'dropdown-sheet-field--compact' : ''} ${className}`.trim()}

  >

    <span className="dropdown-section-title">{label}</span>

    {children}

  </label>

);



const CloseIcon = () => (

  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">

    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />

  </svg>

);



const SearchIcon = () => (

  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">

    <circle cx="14" cy="14" r="8" />

    <path d="M24 24l-7-7" strokeLinecap="round" />

  </svg>

);



export const HeaderSearch = ({

  defaultFilters = emptyFilters,

  variant = 'default',

  className = '',

}) => {

  const navigate = useNavigate();

  const rootRef = useRef(null);

  const filterMoreRef = useRef(null);



  /** Bộ lọc tìm kiếm hiện tại — nguồn truth trước khi navigate */

  const [filters, setFilters] = useState({ ...emptyFilters, ...defaultFilters });

  const [categories, setCategories] = useState([]);

  const [provinces, setProvinces] = useState([]);



  /** UI state: sheet mobile, popover bộ lọc mở rộng, dropdown category/province */

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [isProvinceOpen, setIsProvinceOpen] = useState(false);

  const t = TEXT.searchBar;

  const filterText = TEXT.property.filters;

  const common = TEXT.common;

  const nav = TEXT.nav;



  /** Đồng bộ filters khi URL/defaultFilters thay đổi từ bên ngoài */

  useEffect(() => {

    setFilters({ ...emptyFilters, ...defaultFilters });

  }, [defaultFilters]);



  /** Tải danh sách loại BĐS cho dropdown category */

  useEffect(() => {

    categoryApi

      .getCategories()

      .then((response) => setCategories(response.data.data || []))

      .catch(() => setCategories([]));

  }, []);



  /** Tải cây tỉnh/thành cho dropdown province */

  useEffect(() => {

    locationApi

      .getLocations()

      .then((response) => setProvinces(response.data.data || []))

      .catch(() => setProvinces([]));

  }, []);



  /** Đóng popover bộ lọc khi click ra ngoài */

  useEffect(() => {

    if (!isPopoverOpen) {

      return undefined;

    }



    const handleClickOutside = (event) => {

      if (

        filterMoreRef.current &&

        !filterMoreRef.current.contains(event.target)

      ) {

        setIsPopoverOpen(false);

      }

    };



    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);

  }, [isPopoverOpen]);



  /** Đóng sheet mobile bằng phím Escape */

  useEffect(() => {

    if (!isMobileOpen) {

      return undefined;

    }



    const handleKeyDown = (event) => {

      if (event.key === 'Escape') {

        setIsMobileOpen(false);

      }

    };



    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);

  }, [isMobileOpen]);



  /** Đóng tất cả dropdown desktop cùng lúc */

  const closeDesktopDropdowns = useCallback(() => {

    setIsPopoverOpen(false);

    setIsCategoryOpen(false);

    setIsProvinceOpen(false);

  }, []);



  useCloseOnScroll(isCategoryOpen || isPopoverOpen || isProvinceOpen, closeDesktopDropdowns);



  /** Khóa scroll body khi sheet mobile đang mở */

  useEffect(() => {

    if (!isMobileOpen) {

      return undefined;

    }



    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';



    return () => {

      document.body.style.overflow = previousOverflow;

    };

  }, [isMobileOpen]);



  /** ID preset giá hiện tại — derive từ min_price/max_price */

  const pricePreset = useMemo(

    () => getPricePresetId(filters.min_price, filters.max_price),

    [filters.max_price, filters.min_price]

  );



  /** Số bộ lọc phụ (diện tích, phòng, tiện ích) đang active */

  const extraFilterCount = useMemo(() => countExtraSearchFilters(filters), [filters]);



  /** Tổng số nhóm bộ lọc active — hiển thị badge trên nút compact mobile */

  const activeFilterCount = useMemo(

    () =>

      [

        filters.category_id,

        filters.province,

        filters.min_price || filters.max_price,

        filters.province || filters.district || filters.ward,

        filters.min_area || filters.max_area,

        ...ROOM_SPEC_FILTER_FIELDS.map(({ field }) => filters[field]),

        ...AMENITY_FILTER_FIELDS.map(({ field }) => filters[field]),

      ].filter(Boolean).length,

    [filters]

  );



  /** Cập nhật một trường filter */

  const updateFilter = (field, value) => {

    setFilters((current) => ({ ...current, [field]: value }));

  };



  /** Chọn preset giá → set min_price và max_price tương ứng */

  const handlePricePresetChange = (presetId) => {

    const { min_price, max_price } = getPricePresetValues(presetId);

    setFilters((current) => ({ ...current, min_price, max_price }));

  };



  /** Xóa các bộ lọc phụ, giữ lại vị trí/danh mục/từ khóa cơ bản */

  const clearExtraFilters = () => {

    setFilters((current) => clearExtraSearchFilters(current));

  };



  /** Chip bộ lọc nâng cao đang active — hiển thị trên sheet mobile để xóa nhanh */

  const advancedFilterChips = useMemo(() => {

    const chips = [];



    if (pricePreset) {

      const label = t.pricePresets.find((option) => option.value === pricePreset)?.label;

      if (label) {

        chips.push({ id: 'price', label });

      }

    }



    if (filters.min_area) {

      chips.push({

        id: 'min_area',

        label: `${filterText.minAreaLabel}: ${filters.min_area}+`,

      });

    }



    if (filters.max_area) {

      chips.push({

        id: 'max_area',

        label: `${filterText.maxAreaLabel}: ${filters.max_area}`,

      });

    }



    ROOM_SPEC_FILTER_FIELDS.forEach(({ field, label }) => {

      if (filters[field]) {

        chips.push({ id: field, label: `${filters[field]}+ ${label}` });

      }

    });



    AMENITY_FILTER_FIELDS.forEach(({ field, label }) => {

      if (filters[field] === '1' || filters[field] === true) {

        chips.push({ id: field, label });

      }

    });



    return chips;

  }, [filters, filterText.minAreaLabel, filterText.maxAreaLabel, pricePreset, t.pricePresets]);



  const removeAdvancedFilterChip = (chipId) => {

    if (chipId === 'price') {

      handlePricePresetChange('');

      return;

    }



    updateFilter(chipId, '');

  };



  /** Navigate sang trang chủ với query string từ filters và đóng các panel */

  const navigateWithFilters = (nextFilters) => {

    navigate(buildHomeSearchPath(nextFilters));

    setIsMobileOpen(false);

    setIsPopoverOpen(false);

  };



  /** Submit form desktop — chuyển filters hiện tại thành URL tìm kiếm */

  const handleSubmit = (event) => {

    event.preventDefault();

    navigateWithFilters(filters);

  };



  /** Autocomplete chọn tin cụ thể → đi thẳng trang chi tiết */

  const handleSelectProperty = (item) => {

    navigate(APP_ROUTES.propertyDetails(item.id));

  };



  /** Autocomplete chọn địa điểm → set province/district/ward và tìm ngay */

  const handleSelectLocation = (item) => {

    const nextFilters = {

      ...filters,

      search: '',

      province: item.province || '',

      district: item.district || '',

      ward: item.ward || '',

    };



    setFilters(nextFilters);

    navigateWithFilters(nextFilters);

  };



  const locationSummary =

    filters.province || filters.district || filters.search || t.locationPlaceholder;



  const categoryLabel =

    categories.find((category) => String(category.id) === String(filters.category_id))?.name ||

    filterText.allCategories;



  /** Các phần tóm tắt bộ lọc hiển thị trên nút compact mobile */

  const summaryParts = [

    filters.province || null,

    filters.category_id ? categoryLabel : null,

    pricePreset ? t.pricePresets.find((option) => option.value === pricePreset)?.label : null,

    ...ROOM_SPEC_FILTER_FIELDS.map(({ field, label }) =>

      filters[field] ? `${filters[field]}+ ${label}` : null

    ),

    ...AMENITY_FILTER_FIELDS.map(({ field, label }) => (filters[field] === '1' ? label : null)),

    filters.min_area ? `${filterText.minAreaLabel}: ${filters.min_area}+` : null,

  ].filter(Boolean);



  const renderFilterFields = (variant = 'default') => (

    <SearchFilterFields

      filters={filters}

      pricePreset={pricePreset}

      onUpdateFilter={updateFilter}

      onPricePresetChange={handlePricePresetChange}

      filterText={filterText}

      searchBarText={t}

      common={common}

      variant={variant}

    />

  );



  return (

    <div className={`header-search-wrap header-search-wrap--${variant} ${className}`.trim()} ref={rootRef}>

      <form className="header-search-bar" onSubmit={handleSubmit}>

        <div className="header-search-field header-search-field--location">

          <SearchAutocomplete

            value={filters.search}

            onChange={(value) => updateFilter('search', value)}

            onSelectProperty={handleSelectProperty}

            onSelectLocation={handleSelectLocation}

            placeholder={t.locationPlaceholder}

            inputClassName="header-search-input"

            panelClassName="header-search-panel"

          />

        </div>



        <span className="header-search-divider" aria-hidden="true" />



        <div className="header-search-field header-search-field--province">

          <ProvinceDropdown

            provinces={provinces}

            value={filters.province}

            onChange={(provinceName) => {

              setFilters((current) => ({

                ...current,

                province: provinceName,

                district: '',

                ward: '',

              }));

            }}

            label={t.provinceLabel}

            allLabel={t.provinceAll}

            placeholder={t.provinceShort}

            closeLabel={common.close}

            isOpen={isProvinceOpen}

            onOpenChange={(open) => {

              setIsProvinceOpen(open);

              if (open) {

                setIsPopoverOpen(false);

                setIsCategoryOpen(false);

              }

            }}

          />

        </div>



        <span className="header-search-divider" aria-hidden="true" />



        <div className="header-search-field header-search-field--category">

          <CategoryDropdown

            categories={categories}

            value={filters.category_id}

            onChange={(categoryId) => updateFilter('category_id', categoryId)}

            label={filterText.categoryLabel}

            allLabel={filterText.allCategories}

            placeholder={t.categoryShort}

            closeLabel={common.close}

            isOpen={isCategoryOpen}

            onOpenChange={(open) => {

              setIsCategoryOpen(open);

              if (open) {

                setIsPopoverOpen(false);

                setIsProvinceOpen(false);

              }

            }}

          />

        </div>



        <span className="header-search-divider header-search-divider--desktop" aria-hidden="true" />



        <div

          ref={filterMoreRef}

          className={`header-search-more ${isPopoverOpen ? 'is-open' : ''}`.trim()}

        >

          <button

            type="button"

            className={`header-search-more-btn ${extraFilterCount ? 'has-value' : ''}`}

            aria-expanded={isPopoverOpen}

            onClick={() => {

              setIsPopoverOpen((open) => !open);

              setIsMobileOpen(false);

              setIsCategoryOpen(false);

              setIsProvinceOpen(false);

            }}

          >

            {t.moreFilters}

            {extraFilterCount > 0 && (

              <span className="header-search-more-count">{extraFilterCount}</span>

            )}

          </button>



          <button type="submit" className="header-search-submit" aria-label={nav.searchButton}>

            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">

              <circle cx="14" cy="14" r="8" />

              <path d="M24 24l-7-7" strokeLinecap="round" />

            </svg>

          </button>



          <AnimatedPanel

            isOpen={isPopoverOpen}

            className="dropdown-shell header-search-popover animated-panel--right"

            role="dialog"

            aria-label={t.popoverTitle}

          >

            <div className="dropdown-shell-header">

              <strong>{t.popoverTitle}</strong>

              <button type="button" className="dropdown-shell-close" onClick={() => setIsPopoverOpen(false)}>

                {common.close}

              </button>

            </div>

            <div className="dropdown-shell-body">{renderFilterFields()}</div>

            <div className="dropdown-shell-footer">

              <button type="button" className="dropdown-shell-clear" onClick={clearExtraFilters}>

                {common.resetFilters}

              </button>

              <button type="submit" className="dropdown-shell-submit">

                {nav.searchButton}

              </button>

            </div>

          </AnimatedPanel>

        </div>

      </form>



      <button

        type="button"

        className="header-search-compact"

        onClick={() => {

          setIsMobileOpen(true);

          setIsPopoverOpen(false);

          setIsCategoryOpen(false);

          setIsProvinceOpen(false);

        }}

        aria-label={nav.searchAnywhere}

      >

        <span className="header-search-compact-icon" aria-hidden="true">

          <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5">

            <circle cx="14" cy="14" r="8" />

            <path d="M24 24l-7-7" strokeLinecap="round" />

          </svg>

        </span>

        <span className="header-search-compact-copy">

          <strong>{locationSummary}</strong>

          <small>

            {summaryParts.length > 0 ? summaryParts.join(' · ') : t.mobileSummaryHint}

          </small>

        </span>

        <span className="header-search-compact-action" aria-hidden="true">

          {activeFilterCount > 0 ? (

            <span className="header-search-badge">{activeFilterCount}</span>

          ) : (

            <span className="header-search-compact-filter-icon">

              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                <path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round" />

              </svg>

            </span>

          )}

        </span>

      </button>



      {createPortal(

        <AnimatedPanel

          isOpen={isMobileOpen}

          className="header-search-mobile-overlay"

          role="presentation"

        >

          <button

            type="button"

            className="header-search-sheet-backdrop"

            aria-label={common.close}

            onClick={() => setIsMobileOpen(false)}

          />

          <div

            className="dropdown-shell header-search-sheet"

            role="dialog"

            aria-modal="true"

            aria-label={t.mobilePanelTitle}

          >

            <div className="dropdown-shell-header header-search-sheet-header">

              <div className="header-search-sheet-handle" aria-hidden="true" />

              <div className="header-search-sheet-header-row">

                <strong>{t.mobilePanelTitle}</strong>

                <button

                  type="button"

                  className="dropdown-shell-close header-search-sheet-close"

                  aria-label={common.close}

                  onClick={() => setIsMobileOpen(false)}

                >

                  <CloseIcon />

                </button>

              </div>

            </div>



            <div className="dropdown-shell-body header-search-sheet-body">

              <section className="header-search-sheet-group" aria-label={t.locationLabel}>

                <p className="header-search-sheet-group-title">{t.locationLabel}</p>

                <SheetField label={nav.searchButton} compact className="dropdown-sheet-field--search">

                  <SearchAutocomplete

                    value={filters.search}

                    onChange={(value) => updateFilter('search', value)}

                    onSelectProperty={handleSelectProperty}

                    onSelectLocation={handleSelectLocation}

                    placeholder={t.locationPlaceholder}

                    inputClassName="dropdown-sheet-input"

                    panelClassName="header-search-panel"

                    closeOnScroll={false}

                  />

                </SheetField>



                <div className="header-search-sheet-row">

                  <SheetField label={t.provinceLabel} compact>

                    <ProvinceDropdown

                      provinces={provinces}

                      value={filters.province}

                      onChange={(provinceName) => {

                        setFilters((current) => ({

                          ...current,

                          province: provinceName,

                          district: '',

                          ward: '',

                        }));

                      }}

                      label={t.provinceLabel}

                      allLabel={t.provinceAll}

                      placeholder={t.provinceShort}

                      variant="sheet"

                    />

                  </SheetField>



                  <SheetField label={filterText.categoryLabel} compact>

                    <CategoryDropdown

                      categories={categories}

                      value={filters.category_id}

                      onChange={(categoryId) => updateFilter('category_id', categoryId)}

                      label={filterText.categoryLabel}

                      allLabel={filterText.allCategories}

                      placeholder={t.categoryShort}

                      variant="sheet"

                    />

                  </SheetField>

                </div>

              </section>



              <section className="header-search-sheet-filters" aria-label={t.popoverTitle}>

                <div className="header-search-sheet-filters-head">

                  <h2 className="header-search-sheet-filters-title">{t.popoverTitle}</h2>

                  {extraFilterCount > 0 && (

                    <span className="header-search-sheet-filters-count">

                      {extraFilterCount} đang lọc

                    </span>

                  )}

                </div>



                {advancedFilterChips.length > 0 && (

                  <div className="header-search-sheet-active-chips">

                    {advancedFilterChips.map((chip) => (

                      <span key={chip.id} className="header-search-sheet-active-chip">

                        {chip.label}

                        <button

                          type="button"

                          aria-label={`${common.close} ${chip.label}`}

                          onClick={() => removeAdvancedFilterChip(chip.id)}

                        >

                          <CloseIcon />

                        </button>

                      </span>

                    ))}

                  </div>

                )}



                {renderFilterFields('sheet')}

              </section>

            </div>



            <div className="dropdown-shell-footer header-search-sheet-footer">

              {extraFilterCount > 0 && (

                <button type="button" className="header-search-sheet-reset" onClick={clearExtraFilters}>

                  {common.resetFilters}

                </button>

              )}

              <button

                type="button"

                className="header-search-sheet-search"

                onClick={() => navigateWithFilters(filters)}

              >

                <SearchIcon />

                <span>{nav.searchButton}</span>

                {activeFilterCount > 0 && (

                  <span className="header-search-sheet-search-count">{activeFilterCount}</span>

                )}

              </button>

            </div>

          </div>

        </AnimatedPanel>,

        document.body

      )}

    </div>

  );

};

